import { t, Trans } from "@lingui/macro";
import cx from "classnames";
import moment from "moment";
import React, { useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";

import type { JoinedReferral } from "domain/referrals/types";
import { formatNumber } from "lib/numbers";
import { shortenAddressOrEns } from "lib/wallets";

import { BottomTablePagination } from "components/Pagination/BottomTablePagination";
import { TableListSkeleton } from "components/Skeleton/Skeleton";
import { TableTd, TableTh, TableTheadTr, TableTr } from "components/Table/Table";
import { TableScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";

import EmptyMessage from "./EmptyMessage";
import { getUSDValue } from "./referralsHelper";

const DEFAULT_PAGE_SIZE = 10;

type Props = {
  referrals?: JoinedReferral[];
};

const ReferralTable = ({ referrals = [] }: Props) => {
  const [page, setPage] = useState(1);
  const isPending = false;
  const currentData = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return referrals.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, referrals]);

  const pageCount = Math.max(1, Math.ceil(referrals.length / DEFAULT_PAGE_SIZE));

  return (
    <div className="mt-[16px] w-full">
      {referrals.length > 0 || isPending ? (
        <>
          <TableScrollFadeContainer>
            <table className="w-full min-w-[960px]">
              <thead>
                <TableTheadTr bordered>
                  <TableTh scope="col">
                    <Trans>Traders Address</Trans>
                  </TableTh>
                  <TableTh scope="col">
                    <Trans>Invite Code</Trans>
                  </TableTh>
                  <TableTh scope="col">
                    <Trans>Date Joined</Trans>
                  </TableTh>
                  <TableTh scope="col">
                    <Trans>Total Volume (USDT)</Trans>
                  </TableTh>
                  <TableTh scope="col">
                    <Trans>Fees Paid(USDT)</Trans>
                  </TableTh>
                  <TableTh scope="col" className="!text-left">
                    <Trans>Your Rewards(USDT)</Trans>
                  </TableTh>
                </TableTheadTr>
              </thead>
              <tbody>
                {isPending ? (
                  <TableListSkeleton Structure={SkeletonStructure} count={DEFAULT_PAGE_SIZE} />
                ) : (
                  <>
                    {currentData.map((item, index) => {
                      return (
                        <TableTr key={index} hoverable={false} bordered={false} height={40}>
                          <TableTd className={cx({ "!pt-16": index === 0 })}>
                            {shortenAddressOrEns(item.address, 13)}
                          </TableTd>
                          <TableTd className={cx({ "!pt-16": index === 0 })}>{item.referralCode || "-"}</TableTd>
                          <TableTd className={cx({ "!pt-16": index === 0 })}>
                            {item.joinedAt > 0 ? moment.unix(item.joinedAt).utc().format("DD-MM-YYYY HH:mm") : "-"}
                          </TableTd>
                          <TableTd className={cx({ "!pt-16": index === 0 })}>
                            ${formatNumber(Number(item?.tradeVolume || 0n), { decimals: 2, isForceDecimal: true })}
                          </TableTd>
                          <TableTd className={cx({ "!pt-16": index === 0 })}>
                            ${formatNumber(Number(item?.totalFee || 0n), { decimals: 2, isForceDecimal: true })}
                          </TableTd>
                          <TableTd
                            className={cx("!text-left", {
                              "!pt-16": index === 0,
                            })}
                          >
                            ${getUSDValue(item?.totalRewards || 0n)}
                          </TableTd>
                        </TableTr>
                      );
                    })}
                    {currentData.length > 0 && currentData.length < DEFAULT_PAGE_SIZE && (
                      // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
                      <tr style={{ height: 40 * (DEFAULT_PAGE_SIZE - currentData.length) }}></tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </TableScrollFadeContainer>
          <BottomTablePagination page={page} pageCount={pageCount} onPageChange={setPage} />
        </>
      ) : (
        <EmptyMessage tooltipText={t`Empty data.`} message={t`No data referrals.`} className="!border-none !p-0" />
      )}
    </div>
  );
};

export default ReferralTable;

const SkeletonStructure = () => {
  return (
    <TableTr hoverable={false} bordered={false} height={40}>
      <TableTd>
        <Skeleton width={120} height={20} />
      </TableTd>
      <TableTd>
        <Skeleton width={90} height={20} />
      </TableTd>
      <TableTd>
        <Skeleton width={100} height={20} />
      </TableTd>
      <TableTd>
        <Skeleton width={100} height={20} />
      </TableTd>
      <TableTd>
        <Skeleton width={100} height={20} />
      </TableTd>
      <TableTd className="!text-left">
        <Skeleton width={100} height={20} />
      </TableTd>
    </TableTr>
  );
};
